//#region    ////////// packages
import styled from 'styled-components';
import { Link } from 'react-router-dom';
//#endregion ////////// packages

//#region    ////////// components
import CustomDropdown from '@/shared/components/inputs/CustomDropdown';
import { UIButtonLink } from '@/shared/components/button';
//#endregion ////////// components

//#region    ////////// utils & hooks
import { useAuthStore } from '@/shared/auth/authStore';
import { cn } from '@/shared/shadcn/lib/utils';
import { useAuth } from '@/shared/hooks/useAuth';
//#endregion ////////// utils & hooks

//#region    ////////// assets
import LogoGatePlusFull from '@/shared/assets/svg/logo/logo-gate_plus-full.svg?react';
import MakersLogo from '@/shared/assets/img/makers-logo.png';
import { Bell, ChevronUp, Menu, X } from 'lucide-react';
import IconLogout from '@/shared/assets/svg/layout/icon-logout.svg?react';
import IconLight from '@/shared/assets/svg/common/icon-light.svg?react';
//#endregion ////////// assets

import { useSidebarStore } from '../_useSidebarStore';
import { USER_ROLE_KR, USER_ROLE } from '@/shared/constants';

const QUICK_GUIDE_URL = {
  [USER_ROLE.ADMIN]: 'https://docs.channel.io/gateacaadmin/ko',
  [USER_ROLE.STAFF]: 'https://docs.channel.io/gateacaadmin/ko',
  [USER_ROLE.INSTRUCTOR]: 'https://docs.channel.io/gateacainst/ko',
};

/** 전역 네비게이션 바 컴포넌트 */
export default function GlobalNavbar() {
  const { logout } = useAuth();
  const userInfo = useAuthStore((state) => state.userInfo);
  const isMobileDrawerOpen = useSidebarStore((s) => s.isMobileDrawerOpen);
  const setMobileDrawerOpen = useSidebarStore((s) => s.setMobileDrawerOpen);

  return (
    <S.Header className='font-pretendard' role='banner'>
      <div className='flex items-center gap-4 md:gap-8'>
        {/* 햄버거 버튼 — 모바일 전용 */}
        <button
          type='button'
          className={cn('tw-flex-center size-9 rounded-lg hover:bg-gray-100', 'md:hidden')}
          onClick={() => setMobileDrawerOpen(!isMobileDrawerOpen)}
          aria-label={isMobileDrawerOpen ? '메뉴 닫기' : '메뉴 열기'}>
          {isMobileDrawerOpen ? <X size={20} className='text-gray-900' /> : <Menu size={20} className='text-gray-900' />}
        </button>
        <h1>
          <Link to='/' aria-label='move to home'>
            <LogoGatePlusFull className='h-7.5 w-[6.69rem]' aria-hidden='true' />
          </Link>
        </h1>

        {/* ACA 배지 — 모바일에서 숨김 */}
        <div className={cn('relative flex w-full gap-1 rounded-[6.25rem] bg-gray-100 p-1', 'hidden md:flex')}>
          <div className='relative flex-1 text-center'>
            <div className='tw-flex-center text-dsm text-basic-white relative z-10 h-10 w-27.5 font-semibold'>ACA</div>
            <div className='bg-basic-black absolute inset-0 z-0 rounded-[6.25rem] shadow-sm' />
          </div>
        </div>
      </div>

      <div className='flex items-center gap-8'>
        {QUICK_GUIDE_URL[userInfo?.userRole as keyof typeof QUICK_GUIDE_URL] && (
          <UIButtonLink
            to={QUICK_GUIDE_URL[userInfo?.userRole as keyof typeof QUICK_GUIDE_URL]}
            target='_blank'
            variant='secondary'
            size='extraSmall'
            shape='square'
            className='flex items-center gap-1.5'>
            <i className='tw-icon-wrap size-5'>
              <IconLight className='text-blue-700' />
            </i>
            <span className='text-dsm font-medium'>Quick Guide</span>
          </UIButtonLink>
        )}

        {/* 알림 버튼 */}
        <button type='button' className='tw-flex-center relative size-9 rounded-lg border border-blue-400 hover:bg-gray-100' aria-label='알림'>
          <Bell strokeWidth={1.5} size={20} className='text-blue-400' />
          <span className='absolute top-1.5 right-1.5 flex'>
            <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75' />
            <span className='relative inline-flex size-1.5 rounded-full bg-red-500' />
          </span>
        </button>

        <CustomDropdown
          options={[
            { label: userInfo?.loginId, value: '__none__' },
            {
              label: (
                <div className='flex items-center gap-2 text-gray-900'>
                  <i className='tw-icon-wrap size-4'>
                    <IconLogout />
                  </i>
                  로그아웃
                </div>
              ),
              value: 'logout',
            },
          ]}
          onSelect={(option: { value: string | null }) => {
            if (option.value === 'logout') logout();
          }}
          align='end'
          alignOffset={-10}
          sideOffset={10}
          className='min-w-60 rounded-md!'
          dropdownContentClassName='py-1'
          optionClassName={cn(
            'leading-[1.5] py-3 px-5 font-medium text-blue-gray-800 rounded-none my-1',
            'hover:font-semibold hover:!bg-support-blue-shade hover:!text-support-blue',
            '[&.custom-dropdown-item-disabled]:text-gray-700',
            '[&:not(.custom-dropdown-item-disabled)]:border-t-[1px]'
          )}>
          <button type='button' className='group flex h-full cursor-pointer items-center gap-4 rounded-xl p-2 outline-none hover:bg-gray-100'>
            {/* 아바타 — 데스크탑에서만 표시 */}
            <div className={cn('border-table-line size-8 overflow-hidden rounded-full border', 'hidden md:block')}>
              <img src={MakersLogo} alt='Makers Logo' className='size-full object-cover' />
            </div>

            {/* 학원명 + 사용자 정보 — 데스크탑 전용 */}
            <div className={cn('flex items-center gap-5', 'hidden md:flex')}>
              <p>{userInfo?.academy?.academyName}</p>
              <i className='bg-table-line h-3 w-px' aria-hidden='true' />
              <p>
                <span className='font-semibold'>{userInfo?.userName}</span>{' '}
                <span>({userInfo?.userRole ? USER_ROLE_KR[userInfo.userRole as keyof typeof USER_ROLE_KR] : ''})</span>
              </p>
            </div>

            {/* 사용자 이름 — 모바일 전용 */}
            <p className={cn('text-sm font-semibold text-gray-900', 'md:hidden')}>{userInfo?.userName}</p>

            <i className='transition-transform duration-300 group-data-[state=open]:rotate-180'>
              <ChevronUp strokeWidth={1.5} size={20} className='text-gray-900' />
            </i>
          </button>
        </CustomDropdown>
      </div>
    </S.Header>
  );
}

// --- --- --- Styled Components --- --- --- //
const S = {
  Header: styled.header`
    grid-row-start: 1;
    grid-column: span 2 / span 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 1rem;
    padding-right: 1rem;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.08);
    background-color: #fefefe;
    overflow: visible;
    border-bottom: 1px solid var(--color-table-line);
    z-index: 51; // LNB 보다(z-index 50) 위에 위치 (낮추면 그럼지 안보임)

    @media (min-width: 768px) {
      padding-left: 2rem;
      padding-right: 3.5rem;
    }
  `,
};
